import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {validationStore} from "../ValidationStore";
import {
    PlacementProcessDocumentDto,
    PlacementProcessDto,
    PlacementProcessOriginDto,
    PlacementProcessStatusDto
} from "../../api/sls";
import {equals} from "../../utils";
import {SlsFormData} from "../../components/page/sls/SlsForm";
import {SlsUserItem, slsUserStore} from "./SlsUserStore";
import {SlsCustomerItem} from "./SlsCustomerStore";

const emptyObject = {
    correlationId: '',
    userNumber: '',
    customerNumber: '',
    documents: [],
    origin: '',
    status: PlacementProcessStatusDto.ACTIVE,
}

class SlsCreateStore {
    get origin(): PlacementProcessOriginDto {
        return this._origin;
    }

    set origin(value: PlacementProcessOriginDto) {
        this._origin = value;
    }

    get status(): PlacementProcessStatusDto {
        return this._status;
    }

    set status(value: PlacementProcessStatusDto) {
        this._status = value;
    }

    get documents(): PlacementProcessDocumentDto[] {
        return this._documents;
    }

    set documents(value: PlacementProcessDocumentDto[]) {
        this._documents = value;
    }

    get customer(): SlsCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: SlsCustomerItem | undefined) {
        this._customer = value;
    }

    get user(): SlsUserItem | undefined {
        return this._user;
    }

    set user(value: SlsUserItem | undefined) {
        this._user = value;
    }

    get correlationId(): string {
        return this._correlationId;
    }

    set correlationId(value: string) {
        this._correlationId = value;
    }

    get creating(): boolean {
        return this._creating;
    }

    set creating(value: boolean) {
        this._creating = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get dirty(): boolean {
        const current = this.build()
        return !equals(emptyObject, current)
    }


    private _creating: boolean = false
    private _valid: boolean = false

    private _correlationId: string = ''
    private _origin: PlacementProcessOriginDto = PlacementProcessOriginDto.SLS
    private _user: SlsUserItem | undefined
    private _customer: SlsCustomerItem | undefined
    private _documents: PlacementProcessDocumentDto[] = []
    private _status: PlacementProcessStatusDto = PlacementProcessStatusDto.ACTIVE

    constructor() {
        makeAutoObservable(this)
    }

    get formData(): SlsFormData {
        const {
            correlationId,
            user,
            customer,
            origin,
            documents,
            status,
        } = this
        return {
            correlationId: correlationId,
            origin: origin,
            user,
            customer,
            documents,
            status
        }
    }

    set formData(data: SlsFormData) {
        const {
            correlationId,
            user,
            customer,
            documents,
            origin,
            status
        } = data
        this.correlationId = correlationId
        this.user = user
        this.customer = customer
        this.documents = documents || []
        this.origin = origin || PlacementProcessOriginDto.SLS
        this.status = status || PlacementProcessStatusDto.ACTIVE
    }

    async create(): Promise<string | void> {
        this.creating = true
        const data = this.build()
        const result = await catchApi(async () => await api.sls.api.createPlacementProcess(data))
        this.creating = false
        return result?.data?.id
    }

    async validate() {
        this.valid = false
        const dto = this.build()
        await validationStore.validate(api.sls.api.validatePlacementProcess, dto)

        this.valid = Boolean(validationStore.valid)
    }

    public async reset() {
        this.correlationId = ''
        this.user = await slsUserStore.getActiveUser()
        this.customer = undefined
        this.origin = PlacementProcessOriginDto.SLS
        this.status = PlacementProcessStatusDto.ACTIVE
        this.documents = []
    }


    public build(): PlacementProcessDto {
        const {
            correlationId,
            user,
            customer,
            status,
            origin,
            documents
        } = this
        return {
            correlationId: correlationId,
            origin: origin,
            username: user?.username!!,
            customerId: customer?.id!!,
            documents: documents,
            status: status
        }
    }

    removeDocument(documentId: string) {
        this.documents = this.documents.filter(item => item.documentId !== documentId);
    }
}

const slsCreateStore = new SlsCreateStore()

export {
    slsCreateStore
}
